import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import "../MoComponent.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../../Header/Header";
import { useLocation, useNavigate } from "react-router-dom";
import { apisPath, paths } from "../../../utils/path";
import { CommonContext } from "../../../context/CommonContext";
import { authHttp } from "../../../services/commonHttpService";
import PatientCard from "../../PatientCard/PatientCard";
import { AuthContext } from "../../../context/AuthContext";
import AppointmentSlotsCard from "../../PatientCard/AppointmentSlotsCard";

// Sub-components
import PatientRegistrationDetails from "./SubComponents/PatientRegistrationDetails";
import TreatmentClinicalSummary from "./SubComponents/TreatmentClinicalSummary";
import ChemoSessionDetails from "./SubComponents/ChemoSessionDetails";

const TreatmentProcedure = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);
  const { userRole } = useContext(AuthContext);

  const patientId = location.state?.patientId;
  const planned_chemo_cycles = location.state?.planned_chemo_cycles;
  const upcoming_on_min = location.state?.upcoming_on_min;
  const patientName = location.state?.patientName;

  // State for master level accordions - only one can be open at a time
  const [expandedMaster, setExpandedMaster] = useState(false);
  // State for chemo sessions - only one can be open at a time
  const [expandedChemo, setExpandedChemo] = useState(false);

  // Data states
  const [patientData, setPatientData] = useState(null);
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [step3Data, setStep3Data] = useState(null);
  const [step4Data, setStep4Data] = useState(null);
  const [step5Data, setStep5Data] = useState(null);
  const [moChemoData, setMoChemoData] = useState(null);

  // Fetch treatment data
  const fetchTreatmentSteps = async () => {
    if (!patientId) return;

    setIsLoading(true);
    try {
      const response = await authHttp.get(
        `${apisPath?.getPatientTreatmentStep}/${patientId}`
      );

      if (response?.data) {
        setPatientData(response?.data?.patientData);
        setStep1Data(response?.data?.MI?.step1?.data);
        setStep2Data(response?.data?.MI?.step2?.data);
        setStep3Data(response?.data?.MI?.step3?.data);
        setStep4Data(response?.data?.MI?.step4?.data);
        setStep5Data(response?.data?.MI?.step5?.data);
        setMoChemoData(response?.data?.MO?.chemo);
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch treatment data",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!patientId) {
      setAlertState({
        open: true,
        title: "Patient ID not found. Please select a patient.",
        btnTxt: "GO BACK",
        status: "fail",
      });
      navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
      return;
    }
    fetchTreatmentSteps();
  }, [patientId]);

  const handleInitiateTreatment = () => {
    navigate(`${paths?.user?.commonPoint}${paths?.user?.treatmentProcedureStep}`, {
      state: { patientId, step5Data, planned_chemo_cycles, upcoming_on_min },
    });
  };

  const handleMasterChange = (panel) => (event, isExpanded) => {
    setExpandedMaster(isExpanded ? panel : false);
    // Optionally reset chemo session expansion when master changes
    setExpandedChemo(false);
  };

  const handleChemoChange = (panel) => (event, isExpanded) => {
    setExpandedChemo(isExpanded ? panel : false);
  };

  return (
    <>
      <Header showFullHeader={false} />
      <Container className="tp-container">
        <Box className="tp-header">
          <Typography className="tp-title">Treatment & Procedure Module</Typography>
          {userRole === 5 && (
            <button className="tp-initiate-btn" onClick={handleInitiateTreatment}>
              Initiate Treatment
            </button>
          )}
        </Box>

        <Box className="row">
          <Box className="col-md-3 col-12 mb-3 mb-md-0">
            <PatientCard patientId={patientId} />
          </Box>
          <AppointmentSlotsCard
            patientId={patientId}
            planned_chemo_cycles={planned_chemo_cycles}
            patientName={patientName}
            handleInitiateTreatment={handleInitiateTreatment}
          />
        </Box>

        {/* 1. Patient Registration Details */}
        <Accordion
          expanded={expandedMaster === "patient_registration"}
          onChange={handleMasterChange("patient_registration")}
          className="custom-acc master-acc"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="tp-section-title">Patient Registration Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PatientRegistrationDetails patientData={patientData} />
          </AccordionDetails>
        </Accordion>

        {/* 2. Treatment & Clinical Summary From TCCC */}
        <Accordion
          expanded={expandedMaster === "treatment_summary"}
          onChange={handleMasterChange("treatment_summary")}
          className="custom-acc master-acc"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="tp-section-title">
              Treatment & Clinical Summary From TCCC
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TreatmentClinicalSummary
              step1Data={step1Data}
              step2Data={step2Data}
              step3Data={step3Data}
              step4Data={step4Data}
              step5Data={step5Data}
            />
          </AccordionDetails>
        </Accordion>

        {/* 3. Chemo Sessions (Parent) */}
        {moChemoData && Object.keys(moChemoData).length > 0 && (
          <Accordion
            expanded={expandedMaster === "chemo_sessions"}
            onChange={handleMasterChange("chemo_sessions")}
            className="custom-acc master-acc"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="tp-section-title">All Chemo Sessions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.entries(moChemoData).map(([chemoKey, chemoData], idx) => (
                <Accordion
                  key={chemoKey}
                  expanded={expandedChemo === chemoKey}
                  onChange={handleChemoChange(chemoKey)}
                  className="custom-acc"
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className="tp-section-title">Chemo Session {idx + 1}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ChemoSessionDetails
                      preChemoData={chemoData?.PreChemo}
                      postChemoData={chemoData?.PostChemo}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Container>
    </>
  );
};

export default TreatmentProcedure;
